import React, { useEffect, useRef, useState } from "react";
import { hasXSSCharacters } from "../../../../helpers/Utils";
import { ChooseSenderStepModel, IChooseSenderStepModel } from "../../../../models/buy-giftcard/choose-card/ChooseSenderStep.model";
import { BuyGiftCardStateEnum } from "../../../../models/enums/BuyGiftCardStateEnum";
import { IBuyGiftCardStepProps } from "../../../../models/interfaces/BuyGiftCardStep.interface";
import { AppSettingContext } from "../../../Application";
import LoadingComponent from "../../../shared/LoadingComponent";

interface IChooseSenderStepProps extends IBuyGiftCardStepProps {
    stepModel: IChooseSenderStepModel;
    isAvailable: boolean;

    handleSubmit(stepModel: IChooseSenderStepModel): void;
}

const ChooseSenderStep = (props: IChooseSenderStepProps) => {
    const { labels } = React.useContext(AppSettingContext);
    const checkboxRef = useRef(null);

    //#region STATE PROPERTIES

    const [stepModel, setStepModel] = useState<IChooseSenderStepModel>({
        fullName: props.stepModel?.fullName ?? ""
    });
    const [isModelValid, setIsModelValid] = useState<boolean>();
    const [isLoading, setIsLoading] = useState(true);
    const [modelErrors, setModelErrors] = useState({
        fullName: ''
    });

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        setIsModelValid(isValid());
    }, [stepModel]);

    useEffect(() => {
        setIsLoading(props.isSaving);
    }, [props.isSaving]);

    useEffect(() => {
        checkboxRef.current.checked = props.activeStep === BuyGiftCardStateEnum.CHOOSE_SENDER;
    }, [props.activeStep]);

    //#endregion

    //#region HANDLERS

    const handleExpandBoxCheck = (): void => {
        if (props.isAvailable) {
            if (checkboxRef.current.checked && props.activeStep !== BuyGiftCardStateEnum.CHOOSE_SENDER) {
                props.handleSetActiveStep(BuyGiftCardStateEnum.CHOOSE_SENDER);
            }
        }
    };

    const handleFullNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;

        if (hasXSSCharacters(value)) {
            event.preventDefault();
        } else {
            let errMsg = '';

            if (!value.trim()) {
                errMsg = labels.get("GiftCard.App.V2.ChooseSender.Error.FieldRequired");
            }

            setModelErrors({ fullName: errMsg });
            setStepModel({ fullName: value });
        }
    };

    const handleSaveAndContinue = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();

        if (!isValid()) {
            return;
        }

        props.handleSubmit(stepModel);
        checkboxRef.current.checked = false;
    };

    //#endregion

    //#region COMMON HELPERS

    const isValid = (): boolean => {
        return ChooseSenderStepModel.isValid(stepModel);
    };

    //#endregion

    return (
        <div className="choose-sender box">
            <input
                type="checkbox"
                name="choose_sender_toggle"
                id="choose_sender_toggle"
                ref={checkboxRef}
                onChange={handleExpandBoxCheck}
                defaultChecked={false}
            />
            <div className="action-card">
                <h4 className="box-heading">
                    <i className="box-icon">
                        <img src="/static/options-card-page/options-card-icon-03.png" alt="icon 03" />
                    </i>
                    {labels.get("GiftCard.App.V2.ChooseSender.Title")}
                    <i className="box-arrow"
                        style={{ cursor: props.isAvailable ? "pointer" : "default" }}
                        onClick={() => {
                            if (props.isAvailable) {
                                checkboxRef.current.checked = !checkboxRef.current.checked;
                                handleExpandBoxCheck();
                            }
                        }}
                    ></i>
                </h4>

                {props.activeStep === BuyGiftCardStateEnum.CHOOSE_SENDER && (
                    <div className="show">
                        {isLoading ? (
                            <LoadingComponent />
                        ) : (
                            <div className="slide-content">
                                <p>{labels.get("GiftCard.App.V2.ChooseSender.Subtitle")}</p>

                                <input
                                    type="text"
                                    name="who-from"
                                    placeholder={labels.get("GiftCard.App.V2.ChooseSender.Placeholder.FullName")}
                                    className={`who-from ${modelErrors.fullName ? "react-input-error" : ""}`}
                                    value={stepModel.fullName ?? ""}
                                    maxLength={100}
                                    onChange={handleFullNameChange}
                                />

                                {modelErrors.fullName && (
                                    <i className="small-fs info react-input-error-message">
                                        {modelErrors.fullName}
                                    </i>
                                )}

                                <button
                                    className="btn btn__full_width btn__primary large-fs btn__large"
                                    onClick={handleSaveAndContinue}
                                    disabled={!isModelValid}
                                >
                                    {labels.get("GiftCard.App.V2.ChooseSender.Button.Submit")}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChooseSenderStep;